import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, tap } from 'rxjs';
import { UserModel } from '@assets/models/user/User.model';
import { UserRoleType } from '@assets/types/UserRole.type';
import { AdminApi, injectApi } from './api';
import { GetFullUserResponse } from '@assets/responses/users/GetFullUserById.response';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  #user$: BehaviorSubject<UserModel | null> = new BehaviorSubject<UserModel | null>(null);
  readonly #api: AdminApi = injectApi();

  get isTransactionManager$(): Observable<boolean> {
    return this.#hasRole$('TRANSACTION_MANAGER');
  }

  get isSupport$() {
    return this.#hasRole$('SUPPORT');
  }

  get isSupportAdmin$(): Observable<boolean> {
    return this.#hasRole$('SUPPORT_ADMIN');
  }

  get isOpenAdmin$(): Observable<boolean> {
    return this.#hasRole$('OPEN_ADMIN');
  }

  get isSuperAdmin$(): Observable<boolean> {
    return this.#hasRole$('SUPER_ADMIN');
  }

  get isPaymentAdmin$(): Observable<boolean> {
    return this.#hasRole$('PAYMENT_ADMIN');
  }

  get isPruneoAdmin$(): Observable<boolean> {
    return this.#hasRole$('PRUNEO_ADMIN');
  }

  get canAccessPruneoModule$(): Observable<boolean> {
    return combineLatest([this.isSuperAdmin$, this.isPruneoAdmin$]).pipe(
      map(([isSuperAdmin, isPruneoAdmin]: [boolean, boolean]): boolean => isSuperAdmin || isPruneoAdmin),
    );
  }

  setUser(user?: UserModel | null): void {
    if (!this.#isAdmin(user)) {
      throw new Error(`Access not allowed`);
    }
    this.#user$.next(user);
  }

  clearUser(): void {
    this.#user$.next(null);
  }

  initUserById$(userUid: string): Observable<void> {
    return this.#api.http$(
      'getUserById',
      { userId: userUid },
    ).pipe(
      tap(({ user }: GetFullUserResponse) => this.setUser(user)),
      map(() => void (0)),
    );
  }

  #isAdmin(user?: UserModel | null): user is UserModel {
    const isSuperAdmin: boolean = !!user?.roles.includes('SUPER_ADMIN');
    const isOpenAdmin: boolean = !!user?.roles.includes('OPEN_ADMIN');
    return isSuperAdmin || isOpenAdmin;
  }

  #hasRole$(role: UserRoleType): Observable<boolean> {
    return this.#user$.pipe(
      filter(Boolean),
      map(user => user.roles.includes(role)),
    );
  }
}
